import API from "./api";

// Vendors/customers/carriers/countries rarely change and are fetched on
// almost every page (often several at once via Promise.all). Without this,
// navigating between pages re-fetches the same lists from scratch every
// time. 60 seconds matches the backend's own in-memory cache window for the
// same data (services/reference_data_cache.py), so this doesn't introduce
// any staleness that doesn't already exist server-side.
const CACHE_TTL_MS = 60 * 1000;

const cache = new Map(); // path -> { data, expiresAt }
const inFlight = new Map(); // path -> Promise, de-dupes concurrent requests for the same path

function getCachedLookup(path) {
  const cached = cache.get(path);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve({ data: cached.data });
  }

  const pending = inFlight.get(path);
  if (pending) {
    return pending;
  }

  const request = API.get(path)
    .then((response) => {
      cache.set(path, { data: response.data, expiresAt: Date.now() + CACHE_TTL_MS });
      inFlight.delete(path);
      return response;
    })
    .catch((error) => {
      inFlight.delete(path);
      throw error;
    });

  inFlight.set(path, request);
  return request;
}

// Each returns a promise resolving to { data } - a drop-in replacement for
// the equivalent API.get(...) call, so existing `.then(res => res.data)` /
// `await ...; response.data` call sites don't need to change shape.
export const getVendors = () => getCachedLookup("/vendors");
export const getCustomers = () => getCachedLookup("/customers");
export const getCarriers = () => getCachedLookup("/carriers");
export const getCountries = () => getCachedLookup("/countries");

// For master-data-style pages that mutate vendors/customers/carriers
// directly (e.g. creating a new customer) - clears the cache so the next
// read reflects the change immediately instead of waiting out the TTL.
export function invalidateLookupCache(path) {
  if (path) {
    cache.delete(path);
    return;
  }
  cache.clear();
}
